import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, from, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { Page } from '../../models/page-wrapper.model';
import { PickerRecentCacheService } from '../../shared/services/picker-recent-cache.service';
import { EntityDropdownPickerAdapter } from '../../shared/components/entity-dropdown-picker/entity-dropdown-picker.model';
import { PickerRecentRecord } from '../../shared/components/entity-dropdown-picker/picker-recent.model';
import { ItemCategory, ItemSubCategory } from './item.model';

@Injectable({
  providedIn: 'root',
})
export class ItemSubCategoryService {
  private apiUrl = `${environment.restApiUrl}item-categories`;
  private readonly pickerEntityKey = 'item-sub-category';

  constructor(
    private http: HttpClient,
    private recentPickerCache: PickerRecentCacheService
  ) {}

  private handleError(error: HttpErrorResponse) {
    if (error.error instanceof ErrorEvent) {
      console.error('An error occurred:', error.error.message);
    } else {
      console.error(`Backend returned code ${error.status}, body was: ${JSON.stringify(error.error)}`);
    }
    return throwError(() => new Error('Something went wrong; please try again later.'));
  }

  getSubCategoryById(categoryId: number, subCategoryId: number): Observable<ItemSubCategory> {
    return this.http
      .get<ItemSubCategory>(`${this.apiUrl}/${categoryId}/sub-categories/${subCategoryId}`)
      .pipe(catchError(this.handleError));
  }

  loadRecentSubCategories(categoryId: number | null | undefined, limit = 10): Observable<ItemSubCategory[]> {
    if (!categoryId) {
      return of([]);
    }
    return from(this.recentPickerCache.loadRecent<ItemSubCategory>(this.getEntityKey(categoryId), limit));
  }

  getFreshSubCategoryById(categoryId: number | null | undefined, id: number | string): Observable<ItemSubCategory> {
    if (!categoryId) {
      return of({ id: Number(id) } as ItemSubCategory);
    }
    return this.getSubCategoryById(categoryId, Number(id));
  }

  rememberSubCategory(categoryId: number | null | undefined, subCategory: ItemSubCategory): Observable<void> {
    const normalized = this.normalizeSubCategory(subCategory);
    if (!categoryId || !normalized.id) {
      return of(void 0);
    }

    const record: PickerRecentRecord<ItemSubCategory> = {
      key: `${this.getEntityKey(categoryId)}::${normalized.id}`,
      entityKey: this.getEntityKey(categoryId),
      userKey: this.getCurrentUserKey(),
      entityId: normalized.id,
      label: this.getLabel(normalized),
      payload: normalized,
      lastUsedAt: Date.now(),
    };

    return from(this.recentPickerCache.remember(record));
  }

  createItemSubCategoryPickerAdapter(categoryId: number | null | undefined): EntityDropdownPickerAdapter<ItemSubCategory> {
    return {
      entityKey: this.getEntityKey(categoryId),
      loadRecent: (limit?: number) => this.loadRecentSubCategories(categoryId, limit),
      search: (query: string, page: number, size: number) => this.searchSubCategories(categoryId, query, page, size),
      getFreshById: (id: number | string) => this.getFreshSubCategoryById(categoryId, id),
      rememberSelection: (subCategory: ItemSubCategory) => this.rememberSubCategory(categoryId, subCategory),
      getId: (subCategory: ItemSubCategory) => subCategory.id!,
      getLabel: (subCategory: ItemSubCategory) => this.getLabel(subCategory),
      getSecondaryLabel: (subCategory: ItemSubCategory) => subCategory.code ? `Code: ${subCategory.code}` : '',
      filterBy: 'name,code',
    };
  }

  private searchSubCategories(
    categoryId: number | null | undefined,
    query: string,
    page = 0,
    size = 25
  ): Observable<Page<ItemSubCategory>> {
    if (!categoryId) {
      return of(this.emptySubCategoryPage(page, size));
    }

    return forkJoin([
      this.searchSubCategoriesByField(categoryId, 'name', query, page, size),
      this.searchSubCategoriesByField(categoryId, 'code', query, page, size),
    ]).pipe(
      map(([namePage, codePage]) => this.mergeSubCategoryPages(namePage, codePage, page, size)),
      catchError(this.handleError)
    );
  }

  private searchSubCategoriesByField(
    categoryId: number,
    field: 'name' | 'code',
    query: string,
    page: number,
    size: number
  ): Observable<Page<ItemSubCategory>> {
    const params = {
      [field]: query,
      page,
      size,
    };

    return this.http.get<Page<ItemSubCategory>>(`${this.apiUrl}/${categoryId}/sub-categories`, { params }).pipe(
      catchError(() => of(this.emptySubCategoryPage(page, size)))
    );
  }

  private mergeSubCategoryPages(
    namePage: Page<ItemSubCategory>,
    codePage: Page<ItemSubCategory>,
    page: number,
    size: number
  ): Page<ItemSubCategory> {
    const merged = new Map<number, ItemSubCategory>();
    [...(namePage.content || []), ...(codePage.content || [])].forEach((subCategory) => {
      const normalized = this.normalizeSubCategory(subCategory);
      if (normalized.id !== undefined && normalized.id !== null) {
        merged.set(normalized.id, normalized);
      }
    });

    const content = Array.from(merged.values()).slice(0, size);
    return {
      ...namePage,
      content,
      number: page,
      size,
      totalElements: merged.size,
      numberOfElements: content.length,
    };
  }

  private emptySubCategoryPage(page: number, size: number): Page<ItemSubCategory> {
    return {
      content: [],
      pageable: {
        pageNumber: page,
        pageSize: size,
        sort: { sorted: false, unsorted: true, empty: true },
        offset: page * size,
        paged: true,
        unpaged: false,
      },
      last: true,
      totalPages: 0,
      totalElements: 0,
      size,
      number: page,
      sort: { sorted: false, unsorted: true, empty: true },
      first: page === 0,
      numberOfElements: 0,
      empty: true,
    };
  }

  private normalizeSubCategory(subCategory: ItemSubCategory): ItemSubCategory {
    return {
      ...subCategory,
      id: subCategory?.id ?? undefined,
    };
  }

  private getEntityKey(categoryId: number | null | undefined): string {
    return `${this.pickerEntityKey}:${categoryId ?? 'any'}`;
  }

  private getLabel(subCategory: ItemSubCategory): string {
    return `${subCategory.name ?? ''}${subCategory.code ? ` - ${subCategory.code}` : ''}`.trim();
  }

  private getCurrentUserKey(): string {
    try {
      const rawUser = sessionStorage.getItem('appUser');
      if (!rawUser) {
        return 'anonymous';
      }

      const parsed = JSON.parse(rawUser);
      return String(parsed?.id ?? 'anonymous');
    } catch {
      return 'anonymous';
    }
  }
}
